/**
 * imagePrompts.ts
 *
 * Port of the backend's image prompt construction pipeline to the frontend.
 * Mirrors `backend/providers/generation/image_pipeline.py`:
 *   - `IMAGE_STYLE_PREFIXES`
 *   - `IMAGE_SUFFIX`
 *   - `_VENUE_ARCHITECTURE_LOCK` / `_MINIMAL_LOCK`
 *   - `get_image_style()`
 *   - `build_image_prompt()`
 *   - Aspect ratio logic from `collect_image_specs()`
 */

// ─── Style prefixes ─────────────────────────────────────────────────────────

export const IMAGE_STYLE_PREFIXES: Record<string, string> = {
  showcase:
    'Photorealistic documentary photograph of a real event in progress ' +
    'at the specific venue described in the prompt (indoor OR outdoor ' +
    'depending on the event — hotel ballroom, conference center, camp, ' +
    'sports field, garden, beach, rooftop, park, etc.), wide shot, ' +
    'natural lighting appropriate to the venue type, believable crowd, ' +
    'real furniture / real terrain, no fantasy elements. ',

  content:
    'Photorealistic documentary photograph of a real event activity ' +
    'happening at the exact venue described in the prompt (indoor OR ' +
    'outdoor — do not force a ballroom if the event is outdoors). ' +
    'Medium shot, natural balanced lighting appropriate to the venue, ' +
    'realistic attendees in clothing appropriate for the event context, ' +
    'no theatrical styling. ' +
    'If a specific venue is referenced in the prompt, shoot there; ' +
    'OTHERWISE use a neutral blurred background — do NOT invent a specific location. ',

  product:
    'Photorealistic documentary photograph of event equipment installed ' +
    'at the specific venue described in the prompt (indoor or outdoor), ' +
    'medium shot with the venue visible around it, natural event ' +
    'lighting appropriate to the location, believable scale and placement. ' +
    'If a specific venue is referenced in the prompt, shoot there; ' +
    'OTHERWISE use a neutral blurred background — do NOT invent a specific location. ',

  venue:
    'Photorealistic architectural / landscape photograph of the real ' +
    'venue described in the prompt — could be an indoor space ' +
    '(ballroom, auditorium, convention hall) or an outdoor setting ' +
    '(camp, garden, park, stadium, beach). Wide-angle, natural ' +
    'lighting, existing fixtures or existing terrain intact. ',

  abstract:
    'Minimalist geometric background, soft gradient, very simple, ' +
    'premium but restrained corporate aesthetic. ',

  minimal:
    'Photorealistic image with a soft gradient or subtly blurred neutral background. ' +
    'Subject isolated, NO specific location, NO architectural detail, ' +
    'minimal environmental cues. Studio-like clean backdrop. ',
};

// ─── Realism suffix ──────────────────────────────────────────────────────────

export const IMAGE_SUFFIX =
  ' STRICT REALISM RULES — enforce these above everything else: ' +
  'the scene MUST look like a photograph taken at a real corporate event. ' +
  'NO fantasy elements. NO magic spheres, glowing orbs, floating objects, ' +
  'impossible physics, surreal compositions, or CGI aesthetic. ' +
  'NO theatrical / stage performance imagery unless the service IS a stage act. ' +
  'NO over-dramatic rim lighting, no neon haze, no heavy cinematic color grading, ' +
  'no shallow dreamy bokeh that erases the venue. The venue must stay visible. ' +
  'The people (if any) must look like ordinary business-event attendees, ' +
  'not models in a fashion shoot. Keep lighting natural and believable. ' +
  'NO TEXT of any kind, no words, no typography, no labels, no watermarks, ' +
  'no logos, no overlaid graphics, no signs, no banners with text, no captions. ' +
  'NO clipart, NO cartoons, NO illustrations, NO painterly style. ' +
  'Photorealistic, sharp focus, looks like it was shot with a normal DSLR.';

// ─── Architecture lock suffixes ──────────────────────────────────────────────

const VENUE_ARCHITECTURE_LOCK =
  ' STRICT ARCHITECTURE LOCK (most important — overrides all other prompt wording): ' +
  'DO NOT modify, repaint, redecorate, or transform walls, ceiling, floor, windows, ' +
  'or any structural surface of the venue. ' +
  'The venue surfaces stay exactly as a real existing venue would look — plain painted ' +
  'walls, standard ceiling, existing floor finish. ' +
  'Event decorations are ONLY free-standing removable props: tables, chairs, banners, ' +
  'balloons, LED screens, floral arrangements placed on the floor. ' +
  'If the subject has no venue context, place it on a PURE WHITE studio background — ' +
  'no walls, no furniture, no room environment. ' +
  "Ignore any wording like 'transformado', 'decorado', 'iluminación teatral', 'mágico', " +
  "'ambiente mágico', 'cristales nevados', 'elementos nevados' — treat those words as null.";

const MINIMAL_LOCK =
  ' Plain white or very light gradient background only. ' +
  'No walls, no room, no location, no environment, no architectural detail.';

// ─── Style determination ─────────────────────────────────────────────────────

export type ImageStyle = 'showcase' | 'venue' | 'product' | 'abstract' | 'minimal' | 'content';

/**
 * Map a slide component type to an image style string.
 * Mirrors `get_image_style()` from `image_pipeline.py`.
 */
export function getImageStyle(component: string): ImageStyle {
  if (component.includes('showcase') || component.includes('full_bleed')) return 'showcase';
  if (component.includes('venue')) return 'venue';
  if (component.includes('product') || component.includes('grid') || component.includes('comparison')) return 'product';
  if (component.includes('hero') || component.includes('cover') || component.includes('section')) return 'minimal';
  return 'content';
}

// ─── Aspect ratio ────────────────────────────────────────────────────────────

const SHOWCASE_COMPONENTS = new Set([
  'showcase_full_bleed',
  'showcase_venue',
  'render_showcase',
  'zona_instagrameable_showcase',
]);

/**
 * Return the appropriate aspect ratio for a slide's image.
 * Showcase/render slides use 16:9 (full-bleed). Others use 3:4 (vertical).
 */
export function getImageAspectRatio(component: string): '16:9' | '3:4' {
  return SHOWCASE_COMPONENTS.has(component) ? '16:9' : '3:4';
}

// ─── Full prompt builder ─────────────────────────────────────────────────────

/**
 * Construct the full image generation prompt with style prefix and realism suffix.
 * Mirrors `build_image_prompt()` from `image_pipeline.py`.
 *
 * @param rawPrompt  - Descriptive text about what the image should show.
 * @param style      - Image style key (from `getImageStyle()`).
 * @param hasVenueRef - Whether the slide/event has a specific venue reference.
 * @returns The complete prompt string ready for Gemini / OpenAI.
 */
export function buildFullImagePrompt(
  rawPrompt: string,
  style: ImageStyle,
  hasVenueRef = false,
): string {
  let prefix: string;
  let lockSuffix: string;

  if (!hasVenueRef && (style === 'content' || style === 'product')) {
    prefix = IMAGE_STYLE_PREFIXES['minimal'];
    lockSuffix = MINIMAL_LOCK;
  } else {
    prefix = IMAGE_STYLE_PREFIXES[style] ?? IMAGE_STYLE_PREFIXES['content'];
    if ((style === 'showcase' || style === 'content' || style === 'product') && hasVenueRef) {
      lockSuffix = VENUE_ARCHITECTURE_LOCK;
    } else if (style === 'minimal') {
      lockSuffix = MINIMAL_LOCK;
    } else {
      lockSuffix = '';
    }
  }

  return prefix + rawPrompt + IMAGE_SUFFIX + lockSuffix;
}

// ─── Raw prompt generator ────────────────────────────────────────────────────

/**
 * Generate a descriptive raw prompt from the slide's metadata.
 * This mimics what Claude would write during the `run_deck()` phase.
 * The raw prompt is what gets shown in the textarea for user editing.
 *
 * @param component - Slide component type (e.g. 'product_showcase_right').
 * @param title     - Slide title text.
 * @param section   - Section name the slide belongs to.
 * @param subtitle  - Optional slide subtitle.
 * @param venue     - Optional venue name from event details.
 * @returns A descriptive prompt in Spanish, ready for user review.
 */
export function generateRawPrompt(
  component: string,
  title: string,
  section: string,
  subtitle?: string,
  venue?: string,
): string {
  const venueContext = venue ? ` en ${venue}` : '';

  // ── Product components ──────────────────────────────────────────────────
  if (component.startsWith('product_showcase_') || component.startsWith('product_grid_')) {
    const subject = title || 'equipo de evento';
    if (component === 'product_showcase_pair' || component.startsWith('product_grid_')) {
      return (
        `Fotografía profesional de producto: ${subject} para evento corporativo${venueContext}, ` +
        'composición limpia sobre fondo neutro, iluminación de estudio comercial, ' +
        'calidad de catálogo premium, múltiples productos visibles en la composición.'
      );
    }
    return (
      `Fotografía profesional de producto: ${subject} para evento corporativo${venueContext}, ` +
      'composición limpia sobre fondo neutro, iluminación de estudio comercial, ' +
      'calidad de catálogo premium, producto como elemento central.'
    );
  }

  // ── Showcase components ─────────────────────────────────────────────────
  if (component === 'showcase_venue') {
    const loc = title || venue || 'venue del evento';
    return (
      `Fotografía arquitectónica profesional de ${loc}, ` +
      'vista amplia mostrando el espacio completo, iluminación natural, ' +
      'mobiliario y decoración existente intactos, sin personas, ' +
      'calidad editorial de arquitectura e interiorismo.'
    );
  }
  if (component === 'showcase_full_bleed' || component === 'render_showcase') {
    const subject = title || section || 'evento corporativo';
    return (
      `Fotografía documental panorámica de ${subject}${venueContext}, ` +
      'toma amplia mostrando el evento en pleno desarrollo, ' +
      'asistentes reales interactuando, decoración corporativa profesional, ' +
      'iluminación natural del venue, formato 16:9 full-bleed.'
    );
  }
  if (component === 'zona_instagrameable_showcase') {
    const subject = title || 'zona instagrameable';
    return (
      `Fotografía de ${subject}${venueContext}, ` +
      'instalación interactiva y visual para fotos de invitados, ' +
      'decoración llamativa con branding corporativo sutil, ' +
      'iluminación profesional de evento, personas tomándose fotos.'
    );
  }
  if (component === 'showcase_image_right_round' || component === 'showcase_image_left_round') {
    const subject = title || section || 'detalle del evento';
    return (
      `Fotografía close-up circular de ${subject}${venueContext}, ` +
      'composición centrada para recorte circular, ' +
      'fondo suavemente desenfocado, iluminación natural equilibrada, ' +
      'calidad editorial corporativa.'
    );
  }
  if (component === 'invitation_showcase') {
    const subject = title || 'invitación del evento';
    return (
      `Diseño de ${subject}, estilo elegante corporativo, ` +
      'composición vertical tipo flyer o invitación formal, ' +
      'colores corporativos del evento, tipografía premium, ' +
      'formato retrato con espacio para texto superpuesto.'
    );
  }

  // ── Content with image ──────────────────────────────────────────────────
  if (component === 'content_image_right' || component === 'content_image_left') {
    const subject = title || section || 'actividad del evento';
    return (
      `Fotografía documental de ${subject}${venueContext}, ` +
      'toma media mostrando la actividad o servicio en contexto real, ' +
      'asistentes corporativos participando, iluminación natural, ' +
      'composición vertical (3:4) para panel lateral de diapositiva.'
    );
  }

  // ── People components ───────────────────────────────────────────────────
  if (component === 'people_composite') {
    return (
      'Retratos profesionales corporativos del equipo organizador, ' +
      'fondo neutro uniforme, iluminación de estudio suave, ' +
      'vestimenta profesional, expresiones naturales y accesibles, ' +
      'composición para recorte individual tipo headshot.'
    );
  }
  if (component === 'people_speaker_bio') {
    const speaker = title || 'ponente';
    return (
      `Retrato profesional corporativo de ${speaker}, ` +
      'fondo neutro gradiente suave, iluminación de estudio, ' +
      'encuadre de cintura para arriba, expresión profesional y accesible, ' +
      'calidad editorial para biografía de speaker.'
    );
  }

  // ── Fallback for unknown image-requiring components ─────────────────────
  const subject = title || section || 'evento corporativo';
  const extra = subtitle ? `, ${subtitle}` : '';
  return (
    `Fotografía profesional de ${subject}${extra}${venueContext}, ` +
    'calidad editorial corporativa, iluminación natural, ' +
    'composición limpia sin texto ni elementos gráficos superpuestos.'
  );
}
